import * as AvatarComponent from "@radix-ui/react-avatar";
import { FaCamera, FaImage, FaPauseCircle } from "react-icons/fa";
import { Loader2 } from "lucide-react";
import { captureVideo } from "@/utils/capture";
import { generateAvatarColor } from "./colors";
import "./styles.css";

export { generateAvatarColor } from "./colors";

interface IProps extends React.PropsWithChildren {
  className?: string;
  name?: string;
  size?: "md" | "lg" | "xl";
  src?: string;
  count?: number;
  onChange?: (files: File[]) => Promise<void>;
  showBorder?: boolean;
  marginEnd?: string;
}

type AvatarChangeHandler = NonNullable<IProps["onChange"]>;

function dataURLtoFile(base64: string, filename: string) {
  const arr = base64.split(",");
  const mime = arr[0].match(/:(.*?);/)?.[1];
  const bstr = atob(arr[arr.length - 1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
}

const FileButton = ({ onChange }: { onChange: AvatarChangeHandler }) => {
  const fileRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <IconButton
        isRound
        variant="solid"
        colorScheme="teal"
        aria-label="Change Avatar"
        fontSize="12px"
        width="5"
        height="5"
        minW="5"
        icon={<FaImage />}
        onClick={() => {
          fileRef.current?.click();
        }}
      />
      <input
        ref={fileRef}
        accept="image/*"
        className="hidden"
        type="file"
        onChange={() => {
          const files = fileRef.current?.files;
          if (files) {
            onChange(Array.from(files));
          }
        }}
      />
    </>
  );
};

export const AvatarEditButton = ({
  onChange,
}: {
  onChange?: AvatarChangeHandler;
}) => {
  const [captureLoading, setCaptureLoading] = useState(false);
  const { videoRef, startCamera, stopCamera, isStreaming } = useCamera();
  const handleStopCamera = async () => {
    const { base64 } = captureVideo(videoRef.current!, "image/jpeg");
    const blob = dataURLtoFile(base64, "avatar.jpg");
    setCaptureLoading(true);
    await onChange?.([blob]);
    setTimeout(() => {
      stopCamera();
      setCaptureLoading(false);
    }, 1000);
  };
  const isLaptop = useMediaQuery("(min-width: 768px)");
  if (!onChange) return null;
  return (
    <>
      {captureLoading && (
        <Loader2 className="flex absolute top-[calc(50%-16px)] left-[calc(50%-16px)] text-2xl w-8 h-8 text-gray-200 animate-spin dark:text-gray-600" />
      )}
      <video
        ref={videoRef}
        className="w-full h-full flex absolute top-0 rounded-full"
        style={{
          visibility: isStreaming ? "visible" : "hidden",
        }}
      />
      <span className="absolute bottom-1 left-1/2 inline-flex -translate-x-1/2 translate-y-1/2 gap-0.5">
        <FileButton onChange={onChange} />
        {isLaptop &&
          (!isStreaming ? (
            <IconButton
              isRound
              variant="solid"
              colorScheme="teal"
              aria-label="Change Avatar"
              fontSize="12px"
              width="5"
              height="5"
              minW="5"
              icon={<FaCamera />}
              onClick={startCamera}
            />
          ) : (
            <IconButton
              isRound
              variant="solid"
              colorScheme="teal"
              aria-label="Change Avatar"
              fontSize="12px"
              width="5"
              height="5"
              minW="5"
              icon={<FaPauseCircle />}
              onClick={handleStopCamera}
            />
          ))}
      </span>
    </>
  );
};

export const Avatar = ({
  name = "?",
  src,
  size = "md",
  count = 0,
  children,
  onChange,
  showBorder = true,
  marginEnd = "",
  ...args
}: IProps) => {
  const countMemo = useMemo(() => {
    if (count < 1 || isNaN(count)) {
      return null;
    }
    let c = count.toString();
    if (count > 99) {
      c = "99+";
    }
    return (
      <span className="absolute bg-rose-600 text-xs px-[0.33rem] text-white rounded-xl top-0 right-[-6px] text-center flex items-center justify-center">
        {c}
      </span>
    );
  }, [count]);
  return (
    <span className="relative">
      <AvatarComponent.Root
        {...args}
        style={{
          backgroundColor: generateAvatarColor(name),
          marginInlineEnd: marginEnd,
        }}
        className={clsx("AvatarRoot", size, {
          "border-2 border-black": showBorder,
        })}
      >
        <AvatarComponent.Image
          className="AvatarImage"
          src={src}
          alt="avatar"
          draggable={false}
        />
        <AvatarComponent.Fallback className="AvatarFallback">
          {name.slice(0, 2)}
        </AvatarComponent.Fallback>
      </AvatarComponent.Root>
      {countMemo}
      <AvatarEditButton onChange={onChange} />
      {children}
    </span>
  );
};
